import React, { useEffect, useState } from "react"
import { Modal, Form, InputGroup, Badge, Button, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { parsedUser } from "../../common/GetCurrentUser"
import API from "../../common/API"

const AssignUser = props => {
  const { t } = useTranslation()
  const user = parsedUser()
  const [records, setRecords] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [assigningId, setAssigningId] = useState(null)

  const bindDataRemote = async () => {
    try {
      setIsLoading(true)
      let Query = "OrganizationId=" + user.OrganizationId
      let requestAPI = await API.getAction("users/list", Query)

      if (requestAPI?.data?.status === "ok") {
        var result = requestAPI.data.response || []
        setRecords(result)
      }
    } catch (e) {
      console.error("Error loading users list:", e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    bindDataRemote()
  }, [])

  const handleAssignUser = async item => {
    const targetId = item ? (item.userId || item.id) : "unassign"
    try {
      setAssigningId(targetId)
      let Query = ""
      switch (props.type) {
        case "priority":
          Query = `placeId=${props.place?.id}&accountId=${item ? targetId : ""}&assignedById=${user.ID}`
          await API.getAction("place/AssignToPriority", Query)
          break
        case "revisit":
          Query = `placeId=${props.place?.id}&userId=${item ? targetId : ""}&assignedBy=${user.ID}`
          await API.getAction("place/AssignUserRevisit", Query)
          break
        default:
          Query = `placeId=${props.place?.id}&userId=${item ? targetId : ""}`
          await API.getAction("place/AssignUser", Query)
          break
      }
      props.toggle()
    } catch (e) {
      console.error("Error assigning user:", e)
    } finally {
      setAssigningId(null)
    }
  }

  // Get current assigned ID based on type
  const getCurrentAssignedId = () => {
    if (!props.place) return null
    if (props.type === "revisit") return props.place.revisitAssignedTo || props.place.RevisitAssignedTo
    if (props.type === "priority") return props.place.priorityAssignedTo
    return props.place.assignedTo || props.place.AssignedTo
  }

  const currentAssignedId = getCurrentAssignedId()

  const getInitials = (firstName, lastName) => {
    const f = (firstName || "").charAt(0).toUpperCase()
    const l = (lastName || "").charAt(0).toUpperCase()
    return f || l ? `${f}${l}` : "US"
  }

  const getAvatarColor = index => {
    const colors = ["#007AFF", "#34C759", "#FF9500", "#AF52DE", "#FF2D55", "#5856D6"]
    return colors[index % colors.length]
  }

  const filteredRecords = records.filter(u => {
    if (!searchTerm.trim()) return true
    const term = searchTerm.toLowerCase()
    const fullName = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase()
    const email = (u.email || u.userName || "").toLowerCase()
    return fullName.includes(term) || email.includes(term)
  })

  const getModalTitle = () => {
    if (props.type === "revisit") return t("Asignar Re-visita Pendiente")
    if (props.type === "priority") return t("Asignar Usuario Prioritario")
    return t("Asignar Usuario a la Obra")
  }

  return (
    <Modal show={props.show} onHide={props.toggle} className="modalRight">
      <Modal.Header closeButton className="border-bottom-0 pb-2 pt-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-bold text-dark">{getModalTitle()}</Modal.Title>
          {props.place?.name && (
            <small className="text-muted d-block mt-1">
              <i className="fas fa-building me-1 text-primary" />
              {props.place.name}
            </small>
          )}
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 py-2 d-flex flex-column flex-grow-1 overflow-hidden">
        {/* Search bar */}
        <InputGroup size="sm" className="mb-3 shadow-sm rounded-3 flex-shrink-0">
          <InputGroup.Text className="bg-light border-end-0 text-muted ps-3">
            <i className="fas fa-search" />
          </InputGroup.Text>
          <Form.Control
            placeholder={t("Buscar por nombre o correo...")}
            className="bg-light border-start-0 py-2"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            disabled={isLoading}
          />
          {searchTerm && (
            <Button variant="light" className="border-start-0 text-muted" onClick={() => setSearchTerm("")}>
              <i className="fas fa-times" />
            </Button>
          )}
        </InputGroup>

        {/* User list or Loading state */}
        <div style={{ flex: 1, overflowY: "auto" }} className="pe-1">
          {isLoading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <Spinner animation="border" variant="primary" className="mb-2" />
              <span className="text-muted small fw-semibold">{t("Cargando lista de usuarios...")}</span>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-5 text-muted small">
              <i className="fas fa-user-slash fa-2x mb-2 opacity-50" />
              <div>{t("No se encontraron usuarios")}</div>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {filteredRecords.map((item, index) => {
                const userId = item.userId || item.id
                const isCurrent = currentAssignedId && String(currentAssignedId) === String(userId)
                const isAssigningThis = assigningId === userId
                const fullName = `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.userName || t("Usuario")
                const email = item.email || item.userName || ""

                return (
                  <div
                    key={userId || index}
                    className={`d-flex align-items-center justify-content-between p-2 px-3 rounded-3 border transition-all ${
                      isCurrent ? "bg-primary bg-opacity-10 border-primary" : "bg-white hover-bg-light"
                    }`}
                  >
                    <div className="d-flex align-items-center gap-3">
                      {/* Avatar */}
                      <div
                        className="rounded-circle text-white fw-bold d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm"
                        style={{
                          width: "38px",
                          height: "38px",
                          backgroundColor: getAvatarColor(index),
                          fontSize: "0.85rem",
                        }}
                      >
                        {getInitials(item.firstName, item.lastName)}
                      </div>

                      {/* Info */}
                      <div>
                        <div className="fw-semibold text-dark fs-6 d-flex align-items-center gap-2">
                          {fullName}
                          {isCurrent && (
                            <Badge bg="success" className="fw-normal small px-2 py-1">
                              ✓ {t("Asignado")}
                            </Badge>
                          )}
                        </div>
                        {email && <small className="text-muted d-block">{email}</small>}
                      </div>
                    </div>

                    {/* Action button */}
                    <Button
                      variant={isCurrent ? "outline-secondary" : "primary"}
                      size="sm"
                      className={`px-3 py-1 fw-semibold rounded-2 ${isCurrent ? "" : "shadow-sm"}`}
                      disabled={isCurrent || assigningId !== null}
                      onClick={() => handleAssignUser(item)}
                    >
                      {isAssigningThis ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          {t("Asignando...")}
                        </>
                      ) : isCurrent ? (
                        t("Actual")
                      ) : (
                        t("Asignar")
                      )}
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-2 pb-3 px-4 d-flex justify-content-between">
        {currentAssignedId ? (
          <Button
            variant="outline-danger"
            size="sm"
            className="rounded-2 px-3"
            disabled={assigningId !== null}
            onClick={() => handleAssignUser(null)}
          >
            {assigningId === "unassign" ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                {t("Desvinculando...")}
              </>
            ) : (
              <>
                <i className="fas fa-user-minus me-1" />
                {t("Desvincular Usuario")}
              </>
            )}
          </Button>
        ) : <div />}

        <Button variant="secondary" size="sm" className="rounded-2 px-3" onClick={props.toggle} disabled={assigningId !== null}>
          {t("Cerrar")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AssignUser
